<script setup>

import Preloader from "@/components/App/preloader.vue";
import {computed, nextTick, onMounted, ref} from "vue";
import Ticker from "@/components/App/ticker.vue";
import HeaderDefault from "@/components/App/header-default/header-default.vue";
import footerDefault from "@/components/App/footer/footer.vue";
import {useCommonComposable} from "@/composables/commonComposable";
import categories from "@/data/categories";
import giftCard from "@/assets/images/gift-card.png";
import giftCard2 from "@/assets/images/gift-card-2.png";
import bgTexture from '@/assets/images/bg-texture.png'
import axios from 'axios';

const { useTypingAnimation } = useCommonComposable();

const isLoading = ref(true);

const getCardSteps = [
    {
        header: 'Выбери номинал',
        label: '',
        noms: ['1000', '2500', '5000', '10000', '25000'],
    },
    {
        header: 'Укажи почту — туда пришлём карту',
        content: `<input name="email" id="email" type="email" placeholder="you@example.com">`
    },
    {
        header: 'Перейди на страницу оплаты',
        content: `<div class="btn default" style="width: 100%; padding: 17px; text-align: center">Оплатить</div><p style="color: #6D6F71; font-size: 12px; margin-top: 10px;">Нажимая «Оплатить», вы соглашаетесь с офертой и политикой обработки персональных данных. Карта придёт на указанный email.</p>`
    }
]

const tickerItems = [
    {
        type: 'text',
        text: '///'
    },
    {
        type: 'text',
        text: 'BALLER SHOP PRESENTS'
    },
    {
        type: 'icon'
    },
    {
        type: 'text',
        text: 'YOUR GIFT CARD'
    },
    {
        type: 'text',
        text: '///'
    },
    {
        type: 'text',
        text: 'BALLER ПРЕДСТАВЛЯЕТ'
    },
    {
        type: 'icon'
    },
    {
        type: 'text',
        text: 'ТВОЯ ПОДАРОЧНАЯ КАРТА'
    },
]

const headerText = useTypingAnimation("You've been",
    {
        typingSpeed: 100,
        restartDelay: 'none',
    }).typedText

const cardNumber = ref('')

const cardDigits = ref(Array(12).fill(''));

const currentInputIndex = ref(0);
const activeInputIndex = ref(0);

const inputRefs = ref([]);

const cardReqResult = ref(null);
const cardError = ref('');
const isChecking = ref(false);

const setInputRef = (el, index) => {
    if (el) {
        inputRefs.value[index] = el;
    }
};

const getCardNumber = () => {
    return cardDigits.value.join('');
};

const handleInput = async (index) => {
    resetCardResult();

    cardDigits.value[index] = cardDigits.value[index]
        .replace(/\D/g, '')
        .slice(0, 1);

    // Если введена цифра — автоматически переходим дальше
    if (
        cardDigits.value[index] &&
        index < cardDigits.value.length - 1
    ) {
        activeInputIndex.value = index + 1;

        await nextTick();

        inputRefs.value[index + 1]?.focus();
    }

    // Проверяем: заполнены ли ВСЕ 12 полей
    const isComplete = cardDigits.value.every(
        digit => digit !== ''
    );

    if (!isComplete) {
        return;
    }

    await checkCard();
};

const isSkippedInput = (index) => {
    if (cardDigits.value[index]) {
        return false;
    }

    return cardDigits.value
        .slice(index + 1)
        .some(digit => digit);
};

const resetCardResult = () => {
    cardReqResult.value = null;
    cardError.value = '';
};

const handleBackspace = (index, event) => {
    resetCardResult();

    // Если текущее поле пустое — возвращаемся назад
    if (
        !cardDigits.value[index] &&
        index > 0
    ) {
        event.preventDefault();

        cardDigits.value[index - 1] = '';

        inputRefs.value[index - 1]?.focus();
    }
};

const handlePaste = async (event) => {
    event.preventDefault();

    resetCardResult();

    const pastedValue = event.clipboardData
        .getData('text')
        .replace(/\D/g, '')
        .slice(0, 12);

    if (!pastedValue) {
        return;
    }

    cardDigits.value = Array(12).fill('');

    pastedValue
        .split('')
        .forEach((digit, index) => {
            cardDigits.value[index] = digit;
        });

    const lastIndex = pastedValue.length - 1;

    await nextTick();

    inputRefs.value[Math.min(lastIndex + 1, 11)]?.focus();

    if (pastedValue.length === 12) {
        await checkCard();
    }
};

const isValidCardNumber = (cardNumber) => {
    // Должно быть ровно 12 цифр
    if (!/^\d{12}$/.test(cardNumber)) {
        return false;
    }

    // Первый символ — тип карты:
    // 1 = bonus
    // 2 = gift
    if (!['1', '2'].includes(cardNumber[0])) {
        return false;
    }

    // Второй символ пока зарезервирован и всегда 0
    if (cardNumber[1] !== '0') {
        return false;
    }

    // Третий + четвёртый символы — месяц
    const month = Number(cardNumber.slice(2, 4));

    if (month < 1 || month > 12) {
        return false;
    }

    // Проверяем Luhn
    const digits = cardNumber
        .split('')
        .map(Number);

    let sum = 0;

    for (let i = 0; i < 12; i++) {
        let digit = digits[i];

        // Для 12-значного номера удваиваем
        // каждую вторую цифру, начиная с первой
        if (i % 2 === 0) {
            digit *= 2;

            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
    }

    return sum % 10 === 0;
};

const checkCard = async () => {
    const cardNumber = getCardNumber();

    cardError.value = '';
    cardReqResult.value = null;

    // Frontend validation
    if (!isValidCardNumber(cardNumber)) {
        cardError.value = 'Некорректно введённый номер карты';
        return;
    }

    isChecking.value = true;

    try {
        const config = { headers: { 'Cache-Control': 'no-cache' }, withCredentials: true };
        const response = await axios.post(
            process.env.VUE_APP_API_URL + '/cards/checkCardBalance',
            {
                cardNum: cardNumber
            },
            config
        );
        cardReqResult.value = response.data
        isChecking.value = false;

    } catch (error) {
        if (error.response?.status === 404) {
            cardError.value = 'Карта не найдена';
        } else if (error.response?.data?.message) {
            cardError.value = error.response.data.message;
        } else {
            cardError.value = 'Не удалось проверить баланс карты';
        }
        isChecking.value = false;
    }
};

onMounted(() => {
    isLoading.value = false
})
</script>

<template>
    <div class="gift-card__header">
        <div class="gift-card__header-track">
            <div v-for="copy in 4" :key="copy" class="gift-card__header-group" :aria-hidden="copy !== 1">
                <template v-for="(item, index) in tickerItems" :key="`${copy}-${index}`">
                    <p v-if="item.type === 'text'">{{ item.text }}</p>
                    <svg v-else width="36" height="29" viewBox="0 0 36 29" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723L23.4245 9.03549ZM14.1593 18.0934L18.7197 23.0604L27.5302 13.6505L21.6307 12.741L14.1593 18.0934ZM5.60399 8.77538L11.478 15.1729L19.5076 9.4206L19.5068 9.41122L18.9961 4.08678H9.10207L5.60399 8.77538Z" fill="#D9D9D9"/>
                        <path d="M22.5723 0.151169L23.4245 9.03549L35.5 10.8969L18.6916 28.8488L0.5 9.03549L7.12796 0.151169H22.5723ZM1.11248 9.00413L18.695 28.1541L34.5435 11.2273L22.9894 9.44627L22.1432 0.623442H7.36487L1.11248 9.00413Z" fill="#D4D4D4"/>
                    </svg>
                </template>
            </div>
        </div>
    </div>
    <header-default></header-default>
    <div class="silver-background">
        <div id="bg-texture" :style="`background: url(${bgTexture})`"></div>
        <div class="container page column-page">
            <div class="gift-card__head-text">
                <div class="gift-card-header__above">
                    <p>{{ headerText }}</p>
                    <h1 id="gift-card__header">SCOUTED.</h1>
                </div>
                <div class="gift-card__images">
                    <img id="gift-card" alt="Изображение: подарочная карта" :src="`${giftCard}`">
                    <img id="gift-card-2" alt="Изображение: подарочная карта" :src="`${giftCard2}`">
                </div>
<!--                <p>Подарочная карта потенциального <span>баллера</span></p>-->
            </div>

            <div class="gift_card__check-balance">
                <p class="gift_card__balance_header">Проверить баланс вашей карты:</p>

                <div class="check-balance__get-result">
                    <div class="check-balance__inputs">
                        <div
                            v-for="(digit, index) in cardDigits"
                            :key="index"
                            class="check-balance__input-wrapper"
                            :class="{
                                'is-filled': digit && !cardReqResult,
                                'is-invalid': isSkippedInput(index) && !cardReqResult,
                                'is-success': cardReqResult
                            }"
                        >
                            <div class="check-balance__input-bg"></div>

                            <input
                                :ref="el => setInputRef(el, index)"
                                v-model="cardDigits[index]"
                                type="text"
                                inputmode="numeric"
                                maxlength="1"
                                autocomplete="off"
                                @focus="activeInputIndex = index"
                                @input="handleInput(index)"
                                @keydown.backspace="handleBackspace(index, $event)"
                                @paste="handlePaste"
                            />
                        </div>
                    </div>
                    <Transition name="check-result">
                        <div v-if="cardReqResult || cardError || isChecking" class="check-balance__result-wrapper">
                            <div class="check-balance__result">
                                <span v-if="isChecking">Проверяем...</span>
                                <div v-if="cardReqResult" class="check-balance__result-success">
                                    <div class="card-balance_text">
                                        <span>Карта успешно найдена. Текущий баланс: {{ cardReqResult.card.balance }}₽.</span>
                                        <span>Можно тратить на любые покупки — укажите номер карты при оформлении заказа.</span>
                                    </div>
                                    <div class="card-balance_transactions" v-if="cardReqResult.transactions?.length">
                                        <div class="card-balance_transactions-header">
                                            <span>Дата</span>
                                            <span>Операция</span>
                                            <span>Счёт</span>
                                        </div>
                                        <div
                                            v-for="transaction in cardReqResult.transactions"
                                            :key="transaction.id"
                                        >
                                            <span>{{ new Date(transaction.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric'}) }}</span>
                                            <span>{{ transaction.description }}</span>
                                            <span>{{ transaction.amount > 0 ? '+' : '' }}{{ transaction.amount }}₽</span>
                                        </div>
                                    </div>
                                </div>
                                <span v-else-if="cardError" class="check-balance__result-error">{{ cardError }}</span>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>

            <div class="gift-card__content" v-reveal>
                <div class="gift-card__description">
                    <span>Описание</span>
                    <h2>Подарочная карта потенциального баллера <i class="cursor">|</i></h2>
                    <div>
                        <p>Путь в футболе начинается со взгляда: увидел в любимом футболисте что-то, чего не увидели другие, в любимых цветах — то родное, чего не понять остальным.</p>
                        <p>Культура джерси давно перестала быть «внутрянкой». Футболки — не только часть коллекции особых фанатов футбола. Джерси можно надеть не только на матч любимой команды.</p>
                        <p>Blokecore-стиль — это модно, джерси — часть повседневной жизни. Это способ выделиться и рассказать окружающим: я часть лучшей в мире игры.</p>
                    </div>
                    <div class="gift-card__features" id="gift-card__features">
                        <div class="gift-card__feature-item">
                            <div class="gift-card__feature-icon">
                                <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0.75 8.75H18.75M14.75 0.75V4.75M4.75 0.75V4.75M14.75 2.75H4.75C3.68913 2.75 2.67172 3.17143 1.92157 3.92157C1.17143 4.67172 0.75 5.68913 0.75 6.75V15.5C0.75 16.5609 1.17143 17.5783 1.92157 18.3284C2.67172 19.0786 3.68913 19.5 4.75 19.5H14.75C15.8109 19.5 16.8283 19.0786 17.5784 18.3284C18.3286 17.5783 18.75 16.5609 18.75 15.5V6.75C18.75 5.68913 18.3286 4.67172 17.5784 3.92157C16.8283 3.17143 15.8109 2.75 14.75 2.75Z" stroke="#1C1C1C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                            <p>На шесть месяцев</p>
                        </div>
                        <div class="vr"></div>
                        <div class="gift-card__feature-item">
                            <div class="gift-card__feature-icon">
                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.9883 1.54888C9.3743 1.63003 8.61602 1.8042 7.5374 2.05311L6.30922 2.33654C5.3982 2.54677 4.77082 2.69252 4.29162 2.85698C3.82899 3.01576 3.56727 3.17228 3.36978 3.36978C3.17228 3.56727 3.01576 3.82899 2.85698 4.29162C2.69252 4.77082 2.54677 5.3982 2.33654 6.30922L2.05311 7.5374C1.8042 8.61602 1.63003 9.3743 1.54888 9.9883C1.46982 10.5865 1.48805 10.9913 1.59358 11.3592C1.69911 11.7272 1.89817 12.0801 2.28226 12.5454C2.67651 13.0231 3.22607 13.5738 4.00882 14.3566L5.83845 16.1862C7.19794 17.5457 8.16533 18.5108 8.997 19.1454C9.8114 19.7667 10.4069 20 11.0123 20C11.6178 20 12.2133 19.7667 13.0276 19.1454C13.8593 18.5108 14.8267 17.5457 16.1862 16.1862C17.5457 14.8267 18.5108 13.8593 19.1454 13.0276C19.7667 12.2133 20 11.6178 20 11.0123C20 10.4069 19.7667 9.8114 19.1454 8.997C18.5108 8.16533 17.5457 7.19794 16.1862 5.83845L14.3566 4.00882C13.5738 3.22607 13.0231 2.67651 12.5454 2.28226C12.0801 1.89817 11.7272 1.69911 11.3592 1.59358C10.9913 1.48805 10.5865 1.46982 9.9883 1.54888ZM9.7918 0.0618107C10.5091 -0.0329893 11.1381 -0.0303093 11.7727 0.151701C12.4074 0.333721 12.9422 0.664821 13.5002 1.12538C14.0397 1.57061 14.6405 2.1714 15.391 2.92197L17.2868 4.81774C18.5974 6.12835 19.6351 7.16598 20.3379 8.08714C21.061 9.0349 21.5 9.947 21.5 11.0123C21.5 12.0776 21.061 12.9897 20.3379 13.9375C19.6351 14.8587 18.5974 15.8963 17.2868 17.2069L17.2069 17.2868C15.8963 18.5974 14.8587 19.6351 13.9375 20.3379C12.9897 21.061 12.0776 21.5 11.0123 21.5C9.947 21.5 9.0349 21.061 8.08714 20.3379C7.16598 19.6351 6.12833 18.5974 4.81771 17.2868L2.92196 15.391C2.1714 14.6405 1.57061 14.0397 1.12538 13.5002C0.664821 12.9422 0.333721 12.4074 0.151701 11.7727C-0.0303093 11.1381 -0.0329893 10.5091 0.0618107 9.7918C0.153451 9.0984 0.344511 8.27048 0.583191 7.23622L0.883851 5.93334C1.08302 5.07023 1.24543 4.36639 1.43821 3.80469C1.63955 3.21806 1.89313 2.7251 2.30912 2.30912C2.7251 1.89313 3.21806 1.63955 3.80469 1.43821C4.36639 1.24543 5.07023 1.08302 5.93335 0.883851L7.23622 0.583191C8.27047 0.344511 9.0984 0.153451 9.7918 0.0618107ZM8.24094 6.74514C7.75278 6.25699 6.96133 6.25699 6.47317 6.74514C5.98502 7.2333 5.98502 8.02476 6.47317 8.51291C6.96133 9.0011 7.75278 9.0011 8.24094 8.51291C8.72909 8.02476 8.72909 7.2333 8.24094 6.74514ZM5.41251 5.68448C6.48645 4.61054 8.22766 4.61054 9.3016 5.68448C10.3755 6.75843 10.3755 8.49963 9.3016 9.5736C8.22766 10.6475 6.48645 10.6475 5.41251 9.5736C4.33857 8.49963 4.33857 6.75843 5.41251 5.68448ZM17.8011 9.7402C18.094 10.0331 18.094 10.5079 17.8011 10.8008L10.8221 17.7801C10.5292 18.073 10.0543 18.073 9.7614 17.7801C9.4685 17.4872 9.4685 17.0123 9.7614 16.7194L16.7404 9.7402C17.0333 9.4473 17.5082 9.4473 17.8011 9.7402Z" fill="#1C1C1C"/>
                                </svg>
                            </div>
                            <p>Номинал до 50000₽</p>
                        </div>
                        <div class="vr"></div>
                        <div class="gift-card__feature-item">
                            <div class="gift-card__feature-icon">
                                <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.5 1.5V4.075C15.3469 4.24812 16.108 4.70843 16.6546 5.37807C17.2012 6.04772 17.4998 6.8856 17.4998 7.75C17.4998 8.6144 17.2012 9.45228 16.6546 10.1219C16.108 10.7916 15.3469 11.2519 14.5 11.425V13.999C15.537 13.997 16.324 13.984 16.96 13.919C17.73 13.841 18.195 13.693 18.556 13.452C18.91 13.215 19.216 12.91 19.452 12.556C19.693 12.195 19.842 11.729 19.92 10.96C20 10.179 20 9.17 20 7.75C20 6.33 20 5.321 19.92 4.54C19.841 3.77 19.693 3.305 19.452 2.944C19.214 2.59026 18.9097 2.28598 18.556 2.048C18.195 1.807 17.729 1.658 16.96 1.58C16.324 1.516 15.537 1.503 14.5 1.5ZM13 14V1.5H8.5V14H13ZM7 14V11.425C6.15311 11.2519 5.392 10.7916 4.8454 10.1219C4.2988 9.45228 4.00025 8.6144 4.00025 7.75C4.00025 6.8856 4.2988 6.04772 4.8454 5.37807C5.392 4.70843 6.15311 4.24812 7 4.075V1.501C5.963 1.503 5.176 1.516 4.54 1.581C3.77 1.659 3.305 1.807 2.944 2.048C2.59026 2.28598 2.28598 2.59026 2.048 2.944C1.807 3.305 1.658 3.771 1.58 4.54C1.5 5.321 1.5 6.33 1.5 7.75C1.5 9.17 1.5 10.179 1.58 10.96C1.659 11.73 1.807 12.195 2.048 12.556C2.285 12.91 2.59 13.216 2.944 13.452C3.305 13.693 3.771 13.842 4.54 13.92C5.176 13.984 5.963 13.997 7 14ZM7 5.628C6.56085 5.78281 6.18055 6.07006 5.91155 6.45013C5.64254 6.8302 5.49808 7.28436 5.49808 7.75C5.49808 8.21564 5.64254 8.6698 5.91155 9.04987C6.18055 9.42994 6.56085 9.71719 7 9.872V5.628ZM14.5 9.872C14.9387 9.71673 15.3185 9.42931 15.5871 9.04929C15.8557 8.66928 15.9999 8.21536 15.9999 7.75C15.9999 7.28464 15.8557 6.83072 15.5871 6.45071C15.3185 6.07069 14.9387 5.78327 14.5 5.628V9.872ZM17.112 0.0879998C18 0.178 18.741 0.368 19.389 0.801C19.908 1.147 20.353 1.592 20.699 2.111C21.133 2.759 21.322 3.501 21.412 4.388C21.5 5.254 21.5 6.339 21.5 7.708V7.792C21.5 9.161 21.5 10.246 21.412 11.112C21.322 12 21.132 12.741 20.7 13.389C20.3523 13.9067 19.907 14.3516 19.389 14.699C18.741 15.133 17.999 15.322 17.112 15.412C16.246 15.5 15.161 15.5 13.792 15.5H7.708C6.339 15.5 5.254 15.5 4.388 15.412C3.5 15.322 2.759 15.132 2.111 14.7C1.59331 14.3523 1.14835 13.907 0.801 13.389C0.367 12.741 0.178 11.999 0.0880001 11.112C5.96046e-08 10.246 0 9.161 0 7.792V7.708C0 6.339 5.96046e-08 5.254 0.0880001 4.388C0.178 3.5 0.368 2.759 0.801 2.111C1.14846 1.59341 1.59341 1.14846 2.111 0.801C2.759 0.367 3.501 0.178 4.388 0.0879998C5.254 -1.78814e-07 6.339 0 7.708 0H13.792C15.161 0 16.246 -1.78814e-07 17.112 0.0879998Z" fill="#1C1C1C"/>
                                </svg>
                            </div>
                            <p>Оплата до 100% покупки</p>
                        </div>
                    </div>
                    <div id="policy-gift-card">
                        <router-link :to="{name: 'page-item', params: {url: 'policy-gift-card'}}">
                            <svg data-v-8296ae54="" class="doc-icn" width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path data-v-8296ae54="" d="M10.667 3.33301V12.9307H0V0H7.33301L10.667 3.33301ZM1.28027 11.6025H9.33301V3.88672L6.78027 1.33301H1.28027V11.6025Z" fill="black"></path></svg>
                            Политика в отношении обработки персональных данных при приобретении и использовании подарочных карт</router-link>
                    </div>
                </div>
                <div class="get-card__steps-container">
                    <h3>Сделать подарок — <br>в три клика</h3>
                    <div class="get-card__items">
                        <div class="get-card__step-item" v-if="getCardSteps" v-for="(item, ind) in getCardSteps">
                            <div class="step-item__header">
                                <p>{{ind+1}}</p>
                                <div class="get-card__str"></div>
                            </div>
                            <div class="step-item__content">
                                <h4 v-if="item.header">{{ item.header }}</h4>

                                <div class="step-item__nominals" v-if="item.noms">
                                    <div class="nominal-item" v-for="n in item.noms">
                                        {{ n }}₽
                                    </div>
                                    <input class="nominal-item" type="number" max="49999" style="width: 11ch; box-sizing: border-box; border: none;" name="nominal" id="nominal" placeholder="<49999₽">
                                </div>

                                <div class="content" v-if="item.header && item.header==='Перейди на страницу оплаты'">
                                    <div class="btn default" style="width: 100%; padding: 17px; text-align: center">Оплатить</div>
                                    <p style="color: #6D6F71; font-size: 12px; margin-top: 10px;">Нажимая «Оплатить», вы соглашаетесь с офертой и <router-link :to="{name: 'page-item', params: {url: 'policy-gift-card'}}" style="color: #6D6F71; text-decoration: underline">политикой обработки персональных данных</router-link>. Карта придёт на указанный email.</p>
                                </div>

                                <div class="content" v-else-if="item.content" v-html="item.content">

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!--<div class="main-page_target-btn-outer">
                    <div class="main-page_target-btn">
                        <p>Подарить</p>
                    </div>
                </div>-->
            </div>
        </div>

        <footer-default></footer-default>
    </div>
</template>

<style scoped lang="scss" src="./style.scss"></style>